import React from 'react';
import { useTranslation } from 'react-i18next';

/** Provider marks, inlined so they need no network request and follow currentColor where sensible. */
function GoogleMark(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
    </svg>
  );
}

function LinkedInMark(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
      <path
        fill="#0A66C2"
        d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"
      />
    </svg>
  );
}

/*
 * Apple's mark, from their published path. It must be used unmodified and
 * unrecoloured except for the two permitted fills (black on light, white on
 * dark) — these buttons are white, so black.
 */
function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="#000000"
        d="M17.05 12.54c-.03-2.7 2.2-4 2.3-4.06-1.25-1.83-3.2-2.08-3.89-2.11-1.66-.17-3.24.97-4.08.97-.84 0-2.14-.95-3.52-.92-1.81.03-3.48 1.05-4.41 2.67-1.88 3.26-.48 8.09 1.35 10.73.9 1.29 1.97 2.74 3.38 2.69 1.35-.06 1.87-.88 3.5-.88 1.63 0 2.09.88 3.52.85 1.45-.03 2.37-1.32 3.26-2.62 1.03-1.5 1.45-2.95 1.47-3.03-.03-.01-2.83-1.09-2.86-4.29zM14.4 4.6c.74-.9 1.24-2.15 1.1-3.4-1.07.04-2.36.71-3.13 1.61-.69.8-1.29 2.07-1.13 3.29 1.19.09 2.42-.61 3.16-1.5z"
      />
    </svg>
  );
}

const MARKS = { google: GoogleMark, linkedin: LinkedInMark, apple: AppleMark };
const LABEL_KEYS = {
  google: 'social.continueGoogle',
  linkedin: 'social.continueLinkedin',
  apple: 'social.continueApple',
};

/* Apple first — see the note in the app's copy of this component. */
const order = (a, b) => (a === 'apple' ? -1 : b === 'apple' ? 1 : 0);

/**
 * Social sign-in entry points.
 *
 * Renders nothing when the backend reports no configured providers, so a
 * half-finished OAuth setup cannot show a button that dead-ends.
 */
export default function SocialButtons({ providers = [], returnTo = '/' }) {
  const { t, i18n } = useTranslation();
  if (!providers.length) return null;

  const start = (provider) => {
    const url = new URL(`/api/v1/auth/oauth/${provider}/start`, window.location.origin);
    url.searchParams.set('returnTo', returnTo);
    url.searchParams.set('locale', i18n.resolvedLanguage);
    // Full-page navigation, not a popup: popups are blocked often enough on
    // mobile Safari that the flow would fail for a meaningful share of users.
    window.location.href = url.toString();
  };

  return (
    <div className="space-y-2.5">
      {[...providers].sort(order).map((provider) => {
        const Mark = MARKS[provider];
        if (!Mark) return null;
        return (
          <button
            key={provider}
            type="button"
            onClick={() => start(provider)}
            className="w-full flex items-center justify-center gap-3 bg-white text-black
                       font-heading font-bold uppercase text-base px-4 py-3.5 rounded-xl
                       hover:bg-white/90 transition"
          >
            <Mark />
            {t(LABEL_KEYS[provider])}
          </button>
        );
      })}

      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full h-px bg-white/10" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card px-3 label-tag text-white/30 text-[10px]">{t('social.or')}</span>
        </div>
      </div>
    </div>
  );
}
